import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Check, Sparkle, FloppyDisk, Upload, X,
  Envelope, ShieldCheck, FilePdf, CircleNotch, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { SECTIONS, MULTI_OPTIONS, RADIO_OPTIONS, BUDGET_BANDS, BUDGET_STATEMENTS, ORGANISATION_TYPES, PROJECT_TYPES } from "@/data/idealab";

const LOCAL_KEY = "appistry.idealab.token";

export default function IdeaLabWizard() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const resumeParam = params.get("resume") || "";

  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  // Step indices: 0 = qualifier, 1 = contact, 2..(1+SECTIONS.length) = sections, last = wrap-up
  const [step, setStep] = useState(0);
  const [contact, setContact] = useState({
    full_name: "", email: "", business_name: "", website: "", telephone: "",
    job_title: "", preferred_contact: "email", referral_source: "",
  });
  const [answers, setAnswers] = useState({});
  const [aiTurns, setAiTurns] = useState([]);
  const [files, setFiles] = useState([]);
  const [consentConfidentiality, setConsentConfidentiality] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiAnswer, setAiAnswer] = useState("");
  const [aiSection, setAiSection] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);

  const TOTAL_STEPS = 2 + SECTIONS.length + 1; // qualifier + contact + 8 sections + wrap
  const QUALIFIER_STEP = 0;
  const CONTACT_STEP = 1;
  const WRAP_STEP = TOTAL_STEPS - 1;
  // Helper: given a step index, is it a section? returns section idx (0-based) or -1
  const sectionIndexForStep = (s) => (s >= 2 && s <= 1 + SECTIONS.length ? s - 2 : -1);

  // ───── Bootstrap ─────
  useEffect(() => {
    (async () => {
      let t = resumeParam || (() => { try { return localStorage.getItem(LOCAL_KEY) || ""; } catch { return ""; } })();
      try {
        if (t) {
          const { data } = await api.get(`/idealab/drafts/${t}`);
          hydrate(data);
          setToken(t);
        } else {
          const { data } = await api.post("/idealab/drafts");
          setToken(data.resume_token);
          try { localStorage.setItem(LOCAL_KEY, data.resume_token); } catch { /* ignore */ }
        }
      } catch {
        try { localStorage.removeItem(LOCAL_KEY); } catch { /* ignore */ }
        const { data } = await api.post("/idealab/drafts");
        setToken(data.resume_token);
        try { localStorage.setItem(LOCAL_KEY, data.resume_token); } catch { /* ignore */ }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function hydrate(d) {
    if (d.contact) setContact((c) => ({ ...c, ...d.contact }));
    if (d.answers) setAnswers((a) => ({ ...a, ...d.answers }));
    if (Array.isArray(d.ai_turns)) setAiTurns(d.ai_turns);
    if (Array.isArray(d.file_ids)) {
      setFiles((d.file_ids || []).map((id, i) => ({ id, name: (d.file_names || [])[i] || "file" })));
    }
    setConsentConfidentiality(!!d.consent_confidentiality);
    setConsentPrivacy(!!d.consent_privacy);
    if (typeof d.current_step === "number") setStep(Math.max(0, Math.min(TOTAL_STEPS - 1, d.current_step - 1)));
  }

  // ───── Autosave ─────
  const saveTimer = useRef(null);
  const lastSent = useRef("");
  const queueSave = useCallback((extraPatch = {}) => {
    if (!token) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void doSave(extraPatch); }, 650);
  }, [token, contact, answers, aiTurns, consentConfidentiality, consentPrivacy, step]); // eslint-disable-line

  async function doSave(extraPatch = {}) {
    if (!token) return;
    const body = {
      answers,
      ai_turns: aiTurns,
      consent_confidentiality: consentConfidentiality,
      consent_privacy: consentPrivacy,
      current_step: step + 1,
      ...extraPatch,
    };
    const fullContact = extraPatch.contact ?? contact;
    if (fullContact && fullContact.full_name && /\S+@\S+\.\S+/.test(fullContact.email || "")) {
      body.contact = fullContact;
    } else {
      delete body.contact;
    }
    const sig = JSON.stringify(body);
    if (sig === lastSent.current) return;
    lastSent.current = sig;
    try {
      await api.put(`/idealab/drafts/${token}`, body);
      setSavedAt(new Date());
    } catch { /* silent */ }
  }

  useEffect(() => { queueSave(); }, [answers, contact, aiTurns, consentConfidentiality, consentPrivacy]); // eslint-disable-line

  // ───── Validation ─────
  const contactValid = useMemo(() => !!contact.full_name && /\S+@\S+\.\S+/.test(contact.email || ""), [contact]);

  // ───── Navigation ─────
  async function goNext() {
    await doSave({ current_step: step + 1 });

    // AI follow-up only at the end of a SECTION step, and only when the AI thinks it's worth it
    const secIdx = sectionIndexForStep(step);
    const isSectionEnd = secIdx >= 0;
    if (isSectionEnd && !aiPrompt) {
      const sectionKey = `section-${secIdx + 1}`;
      setAiBusy(true);
      try {
        const { data } = await api.post(`/idealab/drafts/${token}/ai-followup`, { step: sectionKey });
        const q = (data?.question || "").trim();
        if (q && q.includes("?")) {
          setAiPrompt(q);
          setAiSection(sectionKey);
          setAiBusy(false);
          return;
        }
      } catch { /* silent — AI is optional */ }
      setAiBusy(false);
    }
    advance();
  }

  function advance(skipAI = false) {
    if (aiPrompt && !skipAI && aiAnswer.trim()) {
      setAiTurns((t) => [...t, {
        step: aiSection,
        question: aiPrompt,
        answer: aiAnswer.trim(),
        created_at: new Date().toISOString(),
      }]);
    }
    setAiPrompt(""); setAiAnswer(""); setAiSection("");
    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goBack() {
    setAiPrompt(""); setAiAnswer(""); setAiSection("");
    setStep((s) => Math.max(0, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // ───── Files ─────
  const fileInputRef = useRef(null);
  async function uploadFiles(fileList) {
    if (!token || !fileList?.length) return;
    setUploading(true);
    try {
      for (const f of Array.from(fileList)) {
        const fd = new FormData(); fd.append("file", f);
        try {
          const { data } = await api.post(`/idealab/drafts/${token}/files`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setFiles((arr) => [...arr, { id: data.id, name: data.name, size: data.size }]);
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || `Could not upload ${f.name}`);
        }
      }
    } finally { setUploading(false); }
  }
  async function removeFile(id) {
    if (!token) return;
    try {
      await api.delete(`/idealab/drafts/${token}/files/${id}`);
      setFiles((arr) => arr.filter((x) => x.id !== id));
    } catch { toast.error("Could not remove file"); }
  }

  // ───── Submit ─────
  async function submit() {
    if (!consentConfidentiality || !consentPrivacy) { toast.error("Please tick both consent boxes before submitting."); return; }
    if (!contactValid) { toast.error("Please complete your contact details first."); setStep(CONTACT_STEP); return; }
    setSubmitting(true);
    try {
      await doSave();
      const { data } = await api.post(`/idealab/drafts/${token}/submit`);
      try { localStorage.removeItem(LOCAL_KEY); } catch { /* ignore */ }
      navigate(`/idealab/submitted/${data.reference_number}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not submit. Try again.");
    } finally { setSubmitting(false); }
  }

  // ───── UI ─────
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="idealab-wizard-loading"><CircleNotch size={28} className="animate-spin text-zinc-400" /></div>;
  }

  const progressPct = Math.round(((step + 1) / TOTAL_STEPS) * 100);
  const current = step === QUALIFIER_STEP ? { id: "qualifier", title: "About your idea" }
                : step === CONTACT_STEP ? { id: "contact", title: "About you" }
                : step === WRAP_STEP ? { id: "wrap", title: "Wrap-up" }
                : SECTIONS[sectionIndexForStep(step)];

  const railItems = [
    { id: "qualifier", label: "About your idea" },
    { id: "contact", label: "About you" },
    ...SECTIONS.map((s) => ({ id: s.id, label: s.label })),
    { id: "wrap", label: "Wrap-up" },
  ];

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="idealab-wizard">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="app-container py-4 flex items-center justify-between gap-4">
          <Link to="/idealab" className="flex items-center gap-2.5" data-testid="wizard-home">
            <img src="/appistry-logo.png" alt="Appistry" className="h-7 w-7 object-contain" />
            <div>
              <div className="text-sm font-bold tracking-tight leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>Appistry IdeaLab</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mt-0.5">Discovery session</div>
            </div>
          </Link>
          <div className="flex items-center gap-4 text-xs">
            {savedAt && <span className="text-zinc-500 hidden sm:inline-flex items-center gap-1.5" data-testid="wizard-saved"><FloppyDisk size={14} weight="fill" className="text-emerald-500" />Saved {timeAgo(savedAt)}</span>}
            <span className="font-mono uppercase tracking-widest text-zinc-500 text-[10px]">Step {step + 1} / {TOTAL_STEPS}</span>
          </div>
        </div>
        <div className="h-1 bg-zinc-100"><div className="h-full bg-[hsl(var(--appistry-signal))] transition-all duration-500" style={{ width: `${progressPct}%` }} data-testid="wizard-progress" /></div>
      </header>

      <div className="app-container py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Side rail */}
        <aside className="hidden lg:block lg:col-span-3">
          <div className="sticky top-28 space-y-1">
            {railItems.map((s, i) => {
              const done = i < step;
              const active = i === step;
              return (
                <button
                  key={s.id}
                  onClick={() => { if (i <= CONTACT_STEP || contactValid) setStep(i); else toast.message("Complete the contact step first"); }}
                  className={`w-full text-left flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${active ? "border-[hsl(var(--appistry-signal))] bg-white" : done ? "border-emerald-300 hover:bg-white" : "border-zinc-200 hover:bg-white"}`}
                  data-testid={`wizard-rail-${s.id}`}
                >
                  <span className={`flex items-center justify-center h-6 w-6 text-[10px] font-mono font-bold ${done ? "bg-emerald-500 text-white" : active ? "bg-[hsl(var(--appistry-signal))] text-white" : "bg-zinc-200 text-zinc-600"}`}>
                    {done ? <Check size={12} weight="bold" /> : i + 1}
                  </span>
                  <span className={`text-sm ${active ? "text-zinc-900 font-semibold" : "text-zinc-600"}`}>{s.label}</span>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Form */}
        <main className="lg:col-span-9 max-w-3xl">
          <div className="bg-white border border-zinc-200 p-8 md:p-12">
            <AnimatePresence mode="wait">
              <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.3 }}>
                {step === QUALIFIER_STEP && <QualifierStep answers={answers} setAnswers={setAnswers} />}
                {step === CONTACT_STEP && <ContactStep contact={contact} setContact={setContact} />}
                {sectionIndexForStep(step) >= 0 && (
                  <SectionStep
                    section={SECTIONS[sectionIndexForStep(step)]}
                    index={sectionIndexForStep(step) + 1}
                    answers={answers}
                    setAnswers={setAnswers}
                  />
                )}
                {step === WRAP_STEP && (
                  <WrapStep
                    answers={answers} contact={contact} files={files}
                    setAnswers={setAnswers}
                    consentConfidentiality={consentConfidentiality} setConsentConfidentiality={setConsentConfidentiality}
                    consentPrivacy={consentPrivacy} setConsentPrivacy={setConsentPrivacy}
                    fileInputRef={fileInputRef}
                    uploadFiles={uploadFiles} removeFile={removeFile}
                    uploading={uploading} drag={drag} setDrag={setDrag}
                  />
                )}
              </motion.div>
            </AnimatePresence>

            {/* AI follow-up */}
            <AnimatePresence>
              {(aiBusy || aiPrompt) && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} className="mt-8 border border-zinc-200 bg-zinc-50 p-5 md:p-6" data-testid="wizard-ai-panel">
                  <div className="flex items-start gap-3">
                    <Sparkle size={20} weight="fill" className="text-[hsl(var(--appistry-signal))] mt-0.5" />
                    <div className="flex-1">
                      <div className="overline mb-2">Your Appistry Idea Partner</div>
                      {aiBusy ? (
                        <p className="text-zinc-500 inline-flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Thinking…</p>
                      ) : (
                        <>
                          <p className="text-zinc-800 leading-relaxed whitespace-pre-line">{aiPrompt}</p>
                          <textarea rows={3} value={aiAnswer} onChange={(e) => setAiAnswer(e.target.value)} placeholder="Type a short answer… or skip" className="input-appistry mt-3" data-testid="wizard-ai-answer" />
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button onClick={() => advance(false)} className="btn-signal" data-testid="wizard-ai-continue">Continue <ArrowRight size={14} weight="bold" /></button>
                            <button onClick={() => advance(true)} className="btn-ghost-ink !py-2" data-testid="wizard-ai-skip">Skip and continue</button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Nav */}
            {!aiPrompt && !aiBusy && (
              <div className="mt-10 pt-6 border-t border-zinc-200 flex items-center justify-between">
                <button onClick={goBack} disabled={step === 0} className="btn-ghost-ink disabled:opacity-40" data-testid="wizard-back">
                  <ArrowLeft size={14} weight="bold" /> Back
                </button>
                {step < WRAP_STEP ? (
                  <button onClick={goNext} disabled={step === CONTACT_STEP && !contactValid} className="btn-signal disabled:opacity-40" data-testid="wizard-next">
                    Save & continue <ArrowRight size={14} weight="bold" />
                  </button>
                ) : (
                  <button onClick={submit} disabled={submitting || !consentConfidentiality || !consentPrivacy} className="btn-signal disabled:opacity-50" data-testid="wizard-submit">
                    {submitting ? "Submitting…" : "Submit my idea"} <ArrowRight size={14} weight="bold" />
                  </button>
                )}
              </div>
            )}
          </div>

          {contactValid && (
            <p className="mt-6 text-xs text-zinc-500 flex items-start gap-2" data-testid="wizard-resume-note">
              <Envelope size={14} className="mt-0.5 text-zinc-400" />
              We&apos;ve emailed <strong className="text-zinc-700">{contact.email}</strong> a private link so you can come back anytime in the next 30 days — even from a different device.
            </p>
          )}
        </main>
      </div>
    </div>
  );
}

// ───── Step renderers ─────

function QualifierStep({ answers, setAnswers }) {
  const setA = (k, v) => setAnswers({ ...answers, [k]: v });
  return (
    <div data-testid="wizard-step-qualifier">
      <p className="overline mb-3">Step 1 · A quick steer</p>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>First — what kind of organisation are you?</h2>
      <p className="text-zinc-600 mb-8">Two quick choices help us pitch the rest of this conversation properly. Pick whatever feels closest — you can change your mind later.</p>

      <div className="space-y-8">
        <div>
          <label className="block text-sm font-semibold text-zinc-800 mb-3">Which best describes you?</label>
          <div className="flex flex-wrap gap-2">
            {ORGANISATION_TYPES.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setA("organisation_type", opt)}
                className={`px-4 py-2.5 text-sm border ${answers.organisation_type === opt ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
                data-testid={`wizard-qualifier-org-${qSlug(opt)}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-zinc-800 mb-3">What are you exploring?</label>
          <div className="flex flex-wrap gap-2">
            {PROJECT_TYPES.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => setA("project_type", opt)}
                className={`px-4 py-2.5 text-sm border ${answers.project_type === opt ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
                data-testid={`wizard-qualifier-project-${qSlug(opt)}`}
              >
                {opt}
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-500 mt-3">If you're a club or charity, &ldquo;Managed online presence&rdquo; usually fits best — that's website, email, social, basic payments. Not sure? Pick &ldquo;Not sure yet&rdquo; and we'll figure it out together.</p>
        </div>
      </div>
    </div>
  );
}

function qSlug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function ContactStep({ contact, setContact }) {
  const fields = [
    { k: "business_name",  label: "Business name" },
    { k: "full_name",      label: "Contact name", required: true },
    { k: "email",          label: "Email address", required: true, type: "email" },
    { k: "website",        label: "Website (if applicable)" },
    { k: "telephone",      label: "Telephone" },
    { k: "job_title",      label: "Job title" },
  ];
  return (
    <div data-testid="wizard-step-contact">
      <p className="overline mb-3">Step 2 · About you</p>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Now — who are we speaking with?</h2>
      <p className="text-zinc-600 mb-8">We capture this upfront so we can save your progress and email your discovery pack. You can skip everything optional.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {fields.map((f) => (
          <Field key={f.k} label={f.label} required={f.required}>
            <input type={f.type || "text"} value={contact[f.k] || ""} onChange={(e) => setContact({ ...contact, [f.k]: e.target.value })} className="input-appistry" data-testid={`wizard-contact-${f.k}`} />
          </Field>
        ))}
        <Field label="Preferred contact">
          <select value={contact.preferred_contact} onChange={(e) => setContact({ ...contact, preferred_contact: e.target.value })} className="input-appistry" data-testid="wizard-contact-preferred">
            <option value="email">Email</option><option value="phone">Phone</option><option value="video">Video call</option>
          </select>
        </Field>
        <Field label="How did you hear about us?">
          <input value={contact.referral_source || ""} onChange={(e) => setContact({ ...contact, referral_source: e.target.value })} className="input-appistry" placeholder="LinkedIn, event, referral…" data-testid="wizard-contact-referral" />
        </Field>
      </div>
    </div>
  );
}

function SectionStep({ section, index, answers, setAnswers }) {
  const setA = (k, v) => setAnswers({ ...answers, [k]: v });
  const toggleMulti = (k, opt) => {
    const cur = answers[k] || [];
    setA(k, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  return (
    <div data-testid={`wizard-step-${section.id}`}>
      <p className="overline mb-3">Section {index} of {SECTIONS.length} · {section.label}</p>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{section.title}</h2>
      {section.intro && <p className="text-zinc-600 mb-8">{section.intro}</p>}

      <div className="space-y-7">
        {section.questions.map((q) => {
          // Branching: skip if a "dependsOn" condition isn't satisfied
          if (q.dependsOn && !(answers[q.dependsOn.field] || []).includes?.(q.dependsOn.value) && (answers[q.dependsOn.field] !== q.dependsOn.value)) {
            return null;
          }
          return (
            <QuestionField
              key={q.k}
              q={q}
              value={answers[q.k]}
              onChange={(v) => setA(q.k, v)}
              onToggle={(opt) => toggleMulti(q.k, opt)}
            />
          );
        })}
      </div>
    </div>
  );
}

function QuestionField({ q, value, onChange, onToggle }) {
  const testid = `wizard-q-${q.k}`;
  return (
    <div>
      <label className="block text-sm font-semibold text-zinc-800 mb-1">{q.label}</label>
      {q.hint && <p className="text-xs text-zinc-500 mb-2">{q.hint}</p>}
      {q.type === "text" && (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-appistry" placeholder={q.placeholder} data-testid={testid} />
      )}
      {q.type === "longtext" && (
        <textarea rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-appistry" placeholder={q.placeholder} data-testid={testid} />
      )}
      {q.type === "date" && (
        <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-appistry" data-testid={testid} />
      )}
      {q.type === "choice" && (
        <div className="flex flex-wrap gap-2">
          {(RADIO_OPTIONS[q.options] || q.options || []).map((opt) => (
            <button key={opt} type="button" onClick={() => onChange(opt)}
              className={`px-4 py-2 text-sm border ${value === opt ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
              data-testid={`${testid}-${slug(opt)}`}>
              {opt}
            </button>
          ))}
        </div>
      )}
      {q.type === "multi" && (
        <div className="flex flex-wrap gap-2">
          {(MULTI_OPTIONS[q.options] || q.options || []).map((opt) => {
            const on = (value || []).includes(opt);
            return (
              <button key={opt} type="button" onClick={() => onToggle(opt)}
                className={`px-4 py-2 text-sm border inline-flex items-center gap-2 ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
                data-testid={`${testid}-${slug(opt)}`}>
                {on && <Check size={12} weight="bold" />} {opt}
              </button>
            );
          })}
        </div>
      )}
      {q.type === "budget-bands" && (
        <div className="flex flex-wrap gap-2">
          {BUDGET_BANDS.map((b) => (
            <button key={b} type="button" onClick={() => onChange(b)} className={`px-4 py-2 text-sm border ${value === b ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-200 hover:border-zinc-400"}`} data-testid={`${testid}-${slug(b)}`}>
              {b}
            </button>
          ))}
        </div>
      )}
      {q.type === "budget-statements" && (
        <div className="grid grid-cols-1 gap-2">
          {BUDGET_STATEMENTS.map((s) => (
            <button key={s} type="button" onClick={() => onChange(s)} className={`text-left p-4 border ${value === s ? "border-[hsl(var(--appistry-signal))] bg-zinc-950 text-white" : "border-zinc-200 hover:border-zinc-400 bg-white"}`} data-testid={`${testid}-${slug(s)}`}>
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function WrapStep({ answers, contact, files, setAnswers, consentConfidentiality, setConsentConfidentiality, consentPrivacy, setConsentPrivacy, fileInputRef, uploadFiles, removeFile, uploading, drag, setDrag }) {
  return (
    <div data-testid="wizard-step-wrap">
      <p className="overline mb-3">Last step · Wrap-up</p>
      <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Anything to add, then we&apos;re done.</h2>
      <p className="text-zinc-600 mb-6">Drop in any sketches, mockups, briefs or screenshots if you have them. Up to 10 files, 15 MB each. Then a final consent and you&apos;re submitting.</p>

      <div className="space-y-7">
        <div>
          <label className="block text-sm font-semibold text-zinc-800 mb-1">Is there anything else you&apos;d like Appistry to know?</label>
          <textarea rows={5} value={answers.anything_else || ""} onChange={(e) => setAnswers({ ...answers, anything_else: e.target.value })} className="input-appistry" data-testid="wizard-input-anything_else" />
        </div>

        <div>
          <label className="block text-sm font-semibold text-zinc-800 mb-2">Supporting files (optional)</label>
          <div
            className={`border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${drag ? "border-[hsl(var(--appistry-signal))] bg-zinc-50" : "border-zinc-300 hover:border-zinc-500 bg-zinc-50/50"}`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
            data-testid="wizard-files-dropzone"
          >
            <Upload size={24} className="mx-auto text-zinc-400 mb-2" />
            <p className="font-semibold text-sm">{uploading ? "Uploading…" : "Drag & drop or click to browse"}</p>
            <p className="text-xs text-zinc-500 mt-1">PDF · DOCX · XLSX · Images</p>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="wizard-files-input" />
          </div>
          {files.length > 0 && (
            <ul className="mt-3 divide-y divide-zinc-200 border border-zinc-200" data-testid="wizard-files-list">
              {files.map((f) => (
                <li key={f.id} className="flex items-center justify-between px-4 py-2.5">
                  <span className="flex items-center gap-2 text-sm"><FilePdf size={14} className="text-zinc-400" />{f.name}</span>
                  <button onClick={() => removeFile(f.id)} className="text-zinc-400 hover:text-red-500" aria-label="Remove" data-testid={`wizard-file-remove-${f.id}`}><Trash size={14} /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Summary */}
        <div className="border border-zinc-200 bg-zinc-50 p-5">
          <div className="overline mb-2">Your discovery, at a glance</div>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <SummaryRow label="Contact" value={contact.full_name} />
            <SummaryRow label="Business" value={contact.business_name} />
            <SummaryRow label="Email" value={contact.email} />
            <SummaryRow label="Budget" value={answers.budget_band} />
            <SummaryRow label="Launch" value={answers.target_launch_date} />
            <SummaryRow label="Files" value={files.length ? String(files.length) : "None"} />
          </dl>
        </div>

        {/* Consents */}
        <div className="space-y-3">
          <label className="flex items-start gap-3 text-sm text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={consentConfidentiality} onChange={(e) => setConsentConfidentiality(e.target.checked)} className="mt-1" data-testid="wizard-consent-conf" />
            <span>I confirm Appistry will treat this submission as <strong>confidential</strong> and use it only to evaluate this enquiry.</span>
          </label>
          <label className="flex items-start gap-3 text-sm text-zinc-700 cursor-pointer">
            <input type="checkbox" checked={consentPrivacy} onChange={(e) => setConsentPrivacy(e.target.checked)} className="mt-1" data-testid="wizard-consent-priv" />
            <span>I&apos;m happy for Appistry to store these details and contact me about this idea. I can request deletion at any time.</span>
          </label>
        </div>

        <div className="flex items-start gap-3 text-xs text-zinc-500">
          <ShieldCheck size={14} className="text-emerald-500 mt-0.5" />
          You&apos;ll receive a branded discovery PDF in your inbox the moment you submit — yours to keep.
        </div>
      </div>
    </div>
  );
}

// ───── Helpers ─────

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-zinc-800">{label}{required && <span className="text-[hsl(var(--appistry-signal))] ml-1">*</span>}</span>
      </div>
      {children}
    </label>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div>
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-2">{label}</span>
      <span className="text-zinc-800">{value || "—"}</span>
    </div>
  );
}

function slug(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function timeAgo(d) {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 1000));
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}
